import { memo, useCallback, useState, useRef, useEffect } from "react";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Tooltip, IconButton, Typography, Box, styled } from "@mui/material";
import { tooltipClasses } from "@mui/material/Tooltip";

import PropTypes from "prop-types";

import theme from "themeProvider";

// Styled Tooltip
const LightTooltip = styled(({ className, tooltipSx, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ tooltipSx }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: theme.palette.text.primary,
    boxShadow: `0 0 4px 2px ${theme.palette.grey[200]}`,
    maxWidth: "none",
    ...tooltipSx,
  },
}));

const FGTooltipLabelView = ({ label, tooltipContent, onTooltipOpen }) => {
  const [open, setOpen] = useState(false);
  const iconRef = useRef(null);

  const handleClick = useCallback(
    (e) => {
      e.stopPropagation();
      if (!open) {
        onTooltipOpen?.();
      }
      setOpen((prev) => !prev);
    },
    [onTooltipOpen, open],
  );

  const handleClickOutside = useCallback((e) => {
    if (iconRef.current && !iconRef.current.contains(e.target)) {
      setOpen(false);
    }
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  useEffect(() => {
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleClickOutside, open]);

  return (
    <Box style={{ display: "flex", alignItems: "center" }}>
      <Typography>{label}</Typography>
      <Box ref={iconRef}>
        <LightTooltip
          title={tooltipContent}
          open={open}
          onClose={handleClose}
          disableFocusListener
          disableHoverListener
          disableTouchListener
          placement="bottom"
        >
          <IconButton size="small" onClick={handleClick} aria-label="info">
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </LightTooltip>
      </Box>
    </Box>
  );
};

export default memo(FGTooltipLabelView);

FGTooltipLabelView.propTypes = {
  label: PropTypes.string,
  tooltipContent: PropTypes.element,
  onTooltipOpen: PropTypes.func,
};
